import React from 'react';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Lock, Zap, TrendingUp, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const PaywallModal = ({ isOpen, onClose, quoteData }) => {
  const navigate = useNavigate();

  const handleUpgrade = () => {
    navigate('/pricing');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl p-0 overflow-hidden">
        <div className="grid md:grid-cols-2">
          {/* Left side: Blurred quote preview */}
          <div className="relative bg-slate-100 p-8">
            <div className="absolute inset-0 backdrop-blur-md bg-white/30 z-10 flex items-center justify-center">
              <div className="text-center">
                <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Lock className="w-10 h-10 text-white" />
                </div>
                <p className="text-slate-700 font-semibold text-lg">Your quote is ready</p>
              </div>
            </div>
            
            {/* Blurred quote preview */}
            <div className="filter blur-sm opacity-60">
              <Card className="border-2">
                <CardContent className="p-6">
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Project:</span>
                      <span className="font-semibold">{quoteData?.projectName || 'Interior Painting'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Area:</span>
                      <span className="font-semibold">{quoteData?.squareFeet || '500'} sq ft</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Materials:</span>
                      <span className="font-semibold">${quoteData?.materialCost || '1,250'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Labor:</span>
                      <span className="font-semibold">${quoteData?.laborCost || '750'}</span>
                    </div>
                    <div className="border-t-2 pt-3 flex justify-between text-lg">
                      <span className="font-bold">Total:</span>
                      <span className="font-bold text-green-600">${quoteData?.total || '2,400'}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Right side: The pitch */}
          <div className="p-8 bg-white">
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-4">
                <Zap className="w-8 h-8 text-blue-600" />
                <h2 className="text-3xl font-bold text-slate-900">This Quote Could Make You ${quoteData?.total || '2,400'}</h2>
              </div>
              <p className="text-slate-600 text-lg">
                You've used your 5 free quotes this month. Unlock unlimited quotes for just <span className="font-bold text-blue-600">$19/month</span> and never miss a job again.
              </p>
            </div>

            <div className="space-y-4 mb-8">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                <div>
                  <p className="font-semibold text-slate-900">Unlimited Quotes</p>
                  <p className="text-sm text-slate-600">Generate as many quotes as you need, every month</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                <div>
                  <p className="font-semibold text-slate-900">Remove Branding</p>
                  <p className="text-sm text-slate-600">Professional PDFs with your brand only</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                <div>
                  <p className="font-semibold text-slate-900">Quote History</p>
                  <p className="text-sm text-slate-600">Access all your past quotes anytime</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <TrendingUp className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <p className="font-semibold text-slate-900">Track Your Wins</p>
                  <p className="text-sm text-slate-600">See how much you've quoted and won</p>
                </div>
              </div>
            </div>

            <Button 
              onClick={handleUpgrade}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white text-lg py-6 mb-4 shadow-lg hover:shadow-xl transition-all"
            >
              Unlock & Send This Quote → $19/month
            </Button>

            <p className="text-center text-sm text-slate-500">
              Most contractors upgrade after their first paid job
            </p>

            <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-slate-700 text-center">
                💡 <span className="font-semibold">One job pays for a year.</span> At $19/month, you break even after just one won quote.
              </p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PaywallModal;
